import React, { useEffect, useRef, useState } from "react";
import { Box, Button, Skeleton, Stack, Typography } from "@mui/material";
import {
  ArrowForwardIosOutlined,
  DescriptionOutlined,
  VerifiedUserOutlined,
  HandymanOutlined,
  BarChartOutlined,
} from "@mui/icons-material";
import SearchDropdown from "../../../../Components/SearchDropdown/SearchDropdown";
import { HOMEPAGE } from "../../../../Configurations/SiteCopy";

// Icons for the four hero feature callouts, in the same order as
// HOMEPAGE.HERO_FEATURES (Repairs, Warranties, Permits, Boost).
const FEATURE_ICONS = [
  <DescriptionOutlined key="repairs" sx={{ fontSize: 28 }} />,
  <VerifiedUserOutlined key="warranties" sx={{ fontSize: 28 }} />,
  <HandymanOutlined key="permits" sx={{ fontSize: 28 }} />,
  <BarChartOutlined key="boost" sx={{ fontSize: 28 }} />,
];

const VideoBanner: React.FC = () => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [, setVideoHeight] = useState(0);

  useEffect(() => {
    const updateHeight = () => {
      if (videoRef.current) {
        setVideoHeight(videoRef.current.clientHeight);
      }
    };
    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  const handleVideoLoaded = () => setIsVideoLoaded(true);

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        borderRadius: "20px",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: { xl: 660, lg: 640, md: 600, sm: "auto", xs: "auto" },
        py: { xs: 7, md: 6 },
        backgroundColor: "#0b2a1e",
      }}
    >
      {/* Skeleton loader shown while the video is loading */}
      {!isVideoLoaded && (
        <Skeleton
          animation="wave"
          variant="rectangular"
          sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", borderRadius: "20px", zIndex: 1 }}
        />
      )}

      <video
        ref={videoRef}
        autoPlay
        loop
        muted
        playsInline
        poster="https://simplerhome-public.s3.us-east-2.amazonaws.com/hero-video-poster.jpg"
        onLoadedData={handleVideoLoaded}
        onCanPlay={handleVideoLoaded}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          position: "absolute",
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          borderRadius: "20px",
          opacity: isVideoLoaded ? 1 : 0,
          transition: "opacity 0.3s ease-in-out",
        }}
      >
        <source
          src={"https://simplerhome-public.s3.us-east-2.amazonaws.com/hero_bg_video.mp4"}
          type="video/mp4"
        />
        Your browser does not support the video tag.
      </video>

      {/* Scrim for text legibility over the video */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          zIndex: 1,
          borderRadius: "20px",
          background:
            "linear-gradient(180deg, rgba(6,28,20,.36), rgba(6,28,20,.12) 32%, rgba(6,28,20,.22) 62%, rgba(6,28,20,.5))",
        }}
      />

      {/* Hero content */}
      <Box
        sx={{
          position: "relative",
          zIndex: 2,
          px: 2,
          opacity: isVideoLoaded ? 1 : 0,
          transition: "opacity 0.3s ease-in-out",
        }}
      >
        <Stack direction="column" alignItems="center" spacing={0} sx={{ width: "min(760px, 94vw)", textAlign: "center" }}>
          {/* House-lock mark */}
          <Box
            sx={{
              width: { xs: 78, md: 100 },
              height: { xs: 78, md: 100 },
              mb: 0.5,
              filter: "drop-shadow(0 0 22px rgba(70,200,140,.85))",
            }}
          >
            <svg viewBox="0 0 100 100" width="100%" height="100%" fill="none" stroke="#fff" strokeWidth={6} strokeLinejoin="round" strokeLinecap="round">
              <path d="M15 47 L50 16 L85 47 L78 47 L78 87 L22 87 L22 47 Z" />
              <path d="M64 33 V22 H72 V33" strokeWidth={4.5} />
              <path d="M42 53 v-6 a8 8 0 0 1 16 0 v6" strokeWidth={5} />
              <rect x="37" y="53" width="26" height="25" rx="4" fill="#fff" stroke="none" />
              <circle cx="50" cy="63" r="3" fill="#12734d" stroke="none" />
              <rect x="48.6" y="64" width="2.8" height="8" rx="1.4" fill="#12734d" stroke="none" />
            </svg>
          </Box>

          {/* Headline */}
          <Typography
            variant="h1"
            sx={{
              fontFamily: '"PT Serif", Georgia, "Times New Roman", serif',
              fontWeight: 700,
              color: "#fff",
              lineHeight: 1.04,
              letterSpacing: 0,
              fontSize: { xl: "58px", lg: "52px", md: "40px", sm: "34px", xs: "29px" },
              textShadow: "0 3px 20px rgba(0,0,0,.4)",
            }}
          >
            Find It. Claim It.{" "}
            <Box component="span" sx={{ color: "#5fd39a" }}>
              Lock It.
            </Box>
          </Typography>

          {/* Subhead */}
          <Typography
            sx={{
              color: "#fff",
              fontWeight: 500,
              mt: 1.5,
              mb: 3,
              fontSize: { xl: "21px", lg: "20px", md: "18px", xs: "15px" },
              textShadow: "0 2px 12px rgba(0,0,0,.5)",
            }}
          >
            {HOMEPAGE.HERO_SUBHEAD}
          </Typography>

          {/* Primary CTA — sample Home History Report */}
          <Button
            component="a"
            href={HOMEPAGE.HERO_REPORT_URL}
            target="_blank"
            rel="noopener noreferrer"
            startIcon={<DescriptionOutlined />}
            endIcon={<ArrowForwardIosOutlined sx={{ fontSize: "15px !important" }} />}
            sx={{
              textTransform: "none",
              color: "#fff",
              fontWeight: 700,
              fontSize: { md: "20px", xs: "16px" },
              borderRadius: "40px",
              px: { md: 4, xs: 3 },
              py: 1.6,
              border: "2px solid rgba(255,255,255,.5)",
              background: "linear-gradient(180deg, #2eba82, #0f9d69)",
              boxShadow: "0 14px 40px -8px rgba(15,140,90,.7)",
              "&:hover": { background: "linear-gradient(180deg, #34c98c, #12a86f)" },
            }}
          >
            {HOMEPAGE.HERO_REPORT_CTA}
          </Button>

          {/* or divider */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%", maxWidth: 460, my: 2.5 }}>
            <Box sx={{ flex: 1, height: "1px", bgcolor: "rgba(255,255,255,.6)" }} />
            <Typography sx={{ color: "#fff", fontSize: "16px" }}>or</Typography>
            <Box sx={{ flex: 1, height: "1px", bgcolor: "rgba(255,255,255,.6)" }} />
          </Box>

          {/* Search */}
          <Box sx={{ width: "100%", maxWidth: 560 }}>
            <SearchDropdown />
          </Box>

          {/* Feature callouts */}
          <Stack direction="row" justifyContent="center" flexWrap="wrap" sx={{ mt: 3.5, rowGap: 2 }}>
            {HOMEPAGE.HERO_FEATURES.map((f, i) => (
              <Stack
                key={f.top}
                direction="row"
                alignItems="center"
                spacing={1.2}
                sx={{
                  px: { md: 3, xs: 1.5 },
                  color: "#fff",
                  borderLeft: i === 0 ? "none" : { md: "1px solid rgba(255,255,255,.35)", xs: "none" },
                }}
              >
                {FEATURE_ICONS[i]}
                <Box sx={{ textAlign: "left" }}>
                  <Typography sx={{ fontWeight: 700, fontSize: "15px", lineHeight: 1.15 }}>{f.top}</Typography>
                  <Typography sx={{ fontWeight: 400, fontSize: "15px", lineHeight: 1.15 }}>{f.bottom}</Typography>
                </Box>
              </Stack>
            ))}
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
};

export default VideoBanner;
